import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

import type { Role } from './role.constant';

/**
 * 세션 상태. **메모리에만 둔다** — persist 를 붙이지 않는다.
 * 액세스 토큰은 여기 한 곳에만 있고 HTTP client 는 사본을 갖지 않는다.
 * 리프레시 토큰은 httpOnly 쿠키라 스크립트가 볼 수 없다.
 */
export const BOOT_STATUS = {
  /** 인증 여부를 아직 모른다. 어떤 라우트도 그리지 않는다 */
  BOOTING: 'booting',
  GUEST: 'guest',
  AUTHED: 'authed',
  /** 서버에 닿지 못했다. 로그인 화면으로 보내지 않고 "연결할 수 없음" 을 보인다 */
  UNREACHABLE: 'unreachable',
  /** 점검 중 */
  MAINTENANCE: 'maintenance',
} as const;

export type BootStatus = (typeof BOOT_STATUS)[keyof typeof BOOT_STATUS];

export interface SessionIdentity {
  userId: string;
  /** 활성 역할 (토큰에 든 역할 하나) */
  role: Role;
  /** 계정이 가진 역할 전부 */
  roles: readonly Role[];
  /** 대리 로그인일 때 대리인의 사용자 ID */
  impersonatorId?: string;
  /** 표시 이름(대행 배너 등). 서버가 모르면 없다 */
  name?: string;
}

export interface SessionState {
  status: BootStatus;
  accessToken: string | null;
  identity: SessionIdentity | null;
}

export interface SessionActions {
  authenticate(payload: { accessToken: string; identity: SessionIdentity }): void;
  setGuest(): void;
  setBooting(): void;
  setUnreachable(): void;
  setMaintenance(): void;
  /** 신원 전환이 실패했을 때 전환 전 상태로 되돌린다 */
  restore(snapshot: SessionState): void;
}

const INITIAL_STATE: SessionState = {
  status: BOOT_STATUS.BOOTING,
  accessToken: null,
  identity: null,
};

export const sessionStore = createStore<SessionState & SessionActions>()((set) => ({
  ...INITIAL_STATE,
  authenticate: ({ accessToken, identity }) =>
    set({ status: BOOT_STATUS.AUTHED, accessToken, identity }),
  setGuest: () => set({ status: BOOT_STATUS.GUEST, accessToken: null, identity: null }),
  setBooting: () => set({ status: BOOT_STATUS.BOOTING }),
  // 닿지 못한 것은 세션이 끝난 것이 아니다. 토큰 · 신원을 지우지 않는다.
  setUnreachable: () => set({ status: BOOT_STATUS.UNREACHABLE }),
  restore: (snapshot) => set(snapshot),
  setMaintenance: () => set({ status: BOOT_STATUS.MAINTENANCE }),
}));

export function useSession<T>(selector: (state: SessionState & SessionActions) => T): T {
  return useStore(sessionStore, selector);
}

export const selectBootStatus = (state: SessionState) => state.status;
export const selectIdentity = (state: SessionState) => state.identity;
export const selectRole = (state: SessionState) => state.identity?.role ?? null;
/** 라우트 트리를 다시 마운트하는 key. 신원이 바뀌면 컴포넌트 로컬 상태까지 버린다 */
export const selectIdentityKey = (state: SessionState) =>
  state.identity ? `${state.identity.userId}:${state.identity.role}` : 'guest';

export function getAccessToken(): string | null {
  return sessionStore.getState().accessToken;
}
